/**
 * Live notifications wire → `AutheEvent` coercion.
 *
 * The backend notification publisher pushes the event itself (the same shape the
 * REST history feed returns), but the live path is defensive about the wire:
 * unwrap a JSON string / single `{ payload }` envelope, require an `id` plus at
 * least one event-ish field, and run the shared `normalizeEvent` so a string
 * `criticality_level` ("4") lands on the same numeric level the inbox
 * tabs/badges use. Anything else is dropped (returns `null`).
 *
 * Extracted from the notification adapter so it can be unit-tested in isolation.
 */
import type { AutheEvent } from "@authe/fe-commons/events";
import { normalizeEvent } from "@authe/fe-commons/events";

export function toNotificationEvent(payload: unknown): AutheEvent | null {
  let value: unknown = payload;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (value && typeof value === "object" && "payload" in value && !("id" in value)) {
    value = (value as { payload: unknown }).payload;
  }
  if (!value || typeof value !== "object") return null;
  const obj = value as Record<string, unknown>;
  if (typeof obj.id !== "string" || obj.id === "") return null;
  const eventish = "resource" in obj || "message_summary" in obj || "criticality_level" in obj;
  if (!eventish) return null;
  return normalizeEvent(obj as unknown as AutheEvent);
}
